import React, { useContext, useMemo } from 'react';
import styled, { createGlobalStyle, css, ThemeContext, ThemeProvider } from 'styled-components';
import {
  AgGridThemeOverrides,
  ALLOCATOR_MAIN_WIDTH,
  ALLOCATOR_MAIN_WIDTH_WITH_HISTORICALS,
  ALLOCATOR_TOTAL_WIDTH,
  ALLOCATOR_TOTAL_WIDTH_WITH_HISTORICALS,
  AskVennPanel,
  ConfirmUnsavedChangesModal,
  dimensions,
  PrintContainerDimensions,
  ProxyPickerSidePanel,
  SingleProxyPickerContext,
  StudioAllocationPanel,
  StudioContext,
  StudioEmptyAllocationPanel,
  StudioPrivatesAllocationPanel,
  StudioShimmerBlock,
  UnsavedChangesTrigger,
  useAppPrintMode,
  useVirtualization,
} from 'venn-components';
import { useHasFF, useRedrawCharts } from 'venn-utils';
import type { Theme } from 'venn-ui-kit';
import { ColorUtils, Display1, ExternalActivityListener, GetColor, getTypographyTheme } from 'venn-ui-kit';
import BuilderList from './BuilderList';
import { Content, ContentCover, ContentStyle, LeftPanel, PageContent, TopControl } from './shared';
import { StudioMainLeftPanel } from './studio-main-left-panel';
import { TOP_TOOLBAR_HEIGHT } from './top-bar/shared';
import SetupConfigurationModal from './setup-configuration-modal/SetupConfigurationModal';
import { useRecoilValue, waitForAll } from 'recoil';
import {
  allBlockIdsState,
  analysisViewIdState,
  askVennPanelOpenAtom,
  blockGridStyleState,
  compareColumnOpen,
  CONTENT_PADDING,
  hasAllocationPercentError,
  hasUnsavedChangesInPrivatesAllocator,
  hasUnsavedChangesState,
  hasUnsavedPortfolioChangesInAllocator,
  isAllocatorOpenState,
  isReportState,
  openAllocatorSubject,
  openPrivateAllocatorPortfolio,
  selectedBlockIdState,
  selectedBlocksState,
  studioIsPortrait,
  studioLeftPanelOpen,
  studioPrintOrientationType,
  SyncSubjectsToCustomFields,
  useRecoilValueWithDefault,
  viewPages,
} from 'venn-state';
import { StudioBlockConfigPanelContent } from './studio-right-panel/StudioBlockConfigPanelContent';
import { MultiStudioBlockConfigPanelContent } from './studio-right-panel/MultiStudioBlockConfigPanelContent';
import { InaccessibleSubjectsWarningModal } from './InaccessibleSubjectsWarningModal';
import { isEmpty, noop } from 'lodash';
import { RedesignedTopBar } from './top-bar/RedesignedTopBar';
import { useSingleProxyPickerModal } from '../hooks/useSingleProxyPickerModal';
import { useProxyChangeHandler } from '../hooks/useProxyChangeHandler';
import { default as ProxyXLSExport } from '../components/ProxyXLSExport';
import { default as VdparXLSExport } from '../components/VdparXLSExport';
import AdvisoryFeeCorrection from './advisory-fee-correction/AdvisoryFeeCorrection';

const allBlocksShimmer = <StudioShimmerBlock height={400} />;

/**
 * The main content for both Studio and Report Lab, containing the blocks/pages of the view and configuration side panels.
 *
 * Note: individual context props are passed in one-by-one to prevent unnecessary rerenders from studio context from propagating unnecessarily.
 */
const StudioMainContent = React.memo(function StudioMainContent(props: { inPrintMode: boolean; isReport: boolean }) {
  const theme = useContext(ThemeContext);
  const isVirtualized = useVirtualization();
  const hasRcmRlCustomization = useHasFF('rcm_rl_customization_ff');

  const studioTheme: Theme = useMemo(() => {
    const fallbackThemeName = props.isReport && hasRcmRlCustomization ? 'rcm-report' : 'default';
    const storedThemeName = window.sessionStorage?.getItem('studioTheme');
    const activeThemeName = storedThemeName ?? fallbackThemeName;

    return {
      ...theme,
      Typography: getTypographyTheme(activeThemeName),
    };
  }, [hasRcmRlCustomization, props.isReport, theme]);

  const builderListWithDimensions = React.useCallback(
    ({ width }: { width: number; print: boolean }): JSX.Element => (
      <BuilderList width={Math.floor(width - (props.inPrintMode ? 0 : CONTENT_PADDING * 2))} />
    ),
    [props.inPrintMode],
  );

  const blockIds = useRecoilValue(allBlockIdsState);
  const blockGridStyles = useRecoilValue(waitForAll(blockIds.map((id) => blockGridStyleState(id))));

  return (
    <ThemeProvider theme={studioTheme}>
      {/* TODO(VENN-24677): delete useless StyledExternalActivityListener, but be careful to keep overflow behavior */}
      <AgGridThemeOverrides customFontSizeEnabled blockGridStyles={blockGridStyles} />
      <StyledExternalActivityListener allowOverflow={!isVirtualized} onExternalActivity={noop}>
        <Content>
          <React.Suspense fallback={allBlocksShimmer}>
            <Blocks inPrintMode={props.inPrintMode}>
              <PrintContainerDimensions>{builderListWithDimensions}</PrintContainerDimensions>
            </Blocks>
          </React.Suspense>
        </Content>
      </StyledExternalActivityListener>
    </ThemeProvider>
  );
});

/**
 * Displays side panels of Builder (Studio, Report Lab), such as the left panel, block configuration panel, and allocator panel.
 *
 *  Note: individual context props are passed in one-by-one to prevent unnecessary rerenders caused by studio context from propagating unnecessarily.
 */
const StudioPanels = React.memo(function StudioPanels({
  inPrintMode,
  isLeftBlockConfigPanelOpen,
  isAllocatorPanelOpen,
}: {
  inPrintMode: boolean;
  isLeftBlockConfigPanelOpen: boolean;
  isAllocatorPanelOpen: boolean;
}) {
  const hasBlockAssignment = useHasFF('block_assignment_ff');

  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const singleSelectedBlockId = useRecoilValue(selectedBlockIdState);

  const selectedBlockIds = hasBlockAssignment ? selectedBlocks : [singleSelectedBlockId].filter(Boolean);
  const isPrivateAllocatorOpen = !!useRecoilValue(openPrivateAllocatorPortfolio);
  const isAllocatorOpen = !!useRecoilValue(openAllocatorSubject);
  const hasAllocationError = useRecoilValue(hasAllocationPercentError);

  return (
    <>
      {!inPrintMode && isAllocatorPanelOpen && (
        <>
          <AllocationPanelWrapper>
            {isPrivateAllocatorOpen ? (
              <StudioPrivatesAllocationPanel />
            ) : isAllocatorOpen ? (
              <StudioAllocationPanel />
            ) : (
              <StudioEmptyAllocationPanel />
            )}
          </AllocationPanelWrapper>
          {hasAllocationError && (
            <ContentCover>
              <Display1>To resume analysis, ensure investment allocations sum up to 100%</Display1>
            </ContentCover>
          )}
        </>
      )}
      {!inPrintMode && (
        <LeftPanel>
          <StudioMainLeftPanel />
        </LeftPanel>
      )}
      {!inPrintMode && !isEmpty(selectedBlockIds) && isLeftBlockConfigPanelOpen && (
        <BlockConfigPanelWrapper>
          {hasBlockAssignment && selectedBlockIds.length > 1 ? (
            <MultiStudioBlockConfigPanelContent />
          ) : (
            <StudioBlockConfigPanelContent selectedBlockId={selectedBlockIds[0]!} />
          )}
        </BlockConfigPanelWrapper>
      )}
    </>
  );
});

const StudioContent = React.memo(function StudioContent() {
  const hasHistoricals = useHasFF('historical_portfolios_ff');
  const hasBlockAssignment = useHasFF('block_assignment_ff');

  const isMainLeftPanelOpen = useRecoilValue(studioLeftPanelOpen);
  const reportLabPages = useRecoilValue(viewPages);
  const hasUnsavedReturnsAllocatorChanges = useRecoilValueWithDefault(hasUnsavedPortfolioChangesInAllocator, false);
  const hasUnsavedPrivateAllocatorChanges = useRecoilValueWithDefault(hasUnsavedChangesInPrivatesAllocator, false);
  const hasUnsavedAllocatorChanges = hasUnsavedReturnsAllocatorChanges || hasUnsavedPrivateAllocatorChanges;
  const { inPrintMode } = useAppPrintMode();
  const { afterUnsavedChangesAction, isOpenReportConfigModal, openReportConfigModal, closeReportConfigModal } =
    useContext(StudioContext);
  const viewId = useRecoilValue(analysisViewIdState);
  const isReport = useRecoilValue(isReportState);

  const subjectInAllocator = useRecoilValue(openAllocatorSubject);
  const privatePortfolioInAllocator = useRecoilValue(openPrivateAllocatorPortfolio);

  const isCompareOpen = useRecoilValue(compareColumnOpen);
  const isAllocatorOpen = useRecoilValue(isAllocatorOpenState);
  const isAllocatorPanelOpen = isAllocatorOpen || !!subjectInAllocator || !!privatePortfolioInAllocator;
  const hasAskVenn = useHasFF('ask_venn_ff');
  const isAskVennOpen = useRecoilValue(askVennPanelOpenAtom);

  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const selectedBlockId = useRecoilValue(selectedBlockIdState);
  const isLeftBlockConfigPanelOpen = hasBlockAssignment ? selectedBlocks.length > 0 : !!selectedBlockId;
  const hasUnsavedChanges = useRecoilValueWithDefault(hasUnsavedChangesState, false);
  const hasProxyVisibilityFF = useHasFF('proxy_visibility_ff');
  const modalManager = useSingleProxyPickerModal();
  const handleProxyChange = useProxyChangeHandler();

  const printOrientationType = useRecoilValue(studioPrintOrientationType);
  const isPortrait = useRecoilValue(studioIsPortrait);
  const pageSize = `A4 ${isPortrait ? 'portrait' : 'landscape'}`;

  const { totalHeight: pageHeight } = dimensions[printOrientationType];
  const printContentHeight = isReport ? reportLabPages.length * pageHeight : undefined;

  useRedrawCharts(isLeftBlockConfigPanelOpen, isAllocatorPanelOpen, isAskVennOpen, true);

  return (
    <UnsavedChangesTrigger isNotSaved={hasUnsavedChanges || hasUnsavedAllocatorChanges} ignoreSearchParam>
      {({ isOpen: isUnsavedChangesModalOpen, handleConfirmNavigation, handleReject }) => (
        <>
          <AdvisoryFeeCorrection
            isUnsavedChangesModalActive={isUnsavedChangesModalOpen}
            hasAfterUnsavedChangesAction={!!afterUnsavedChangesAction}
            onProceed={handleConfirmNavigation}
            onReject={handleReject}
          >
            {(isUnsavedChangesModalOpen || afterUnsavedChangesAction) && (
              <ConfirmUnsavedChangesModal onProceed={handleConfirmNavigation} onReject={handleReject} />
            )}
          </AdvisoryFeeCorrection>

          <SyncSubjectsToCustomFields />
          <ContentWrapper className="studio-content-wrapper" height={printContentHeight}>
            <SingleProxyPickerContext.Provider value={modalManager}>
              <StudioStyle pageSize={pageSize} />
              <PageContent
                allocatorPanelWidth={
                  isCompareOpen
                    ? hasHistoricals
                      ? ALLOCATOR_TOTAL_WIDTH_WITH_HISTORICALS
                      : ALLOCATOR_TOTAL_WIDTH
                    : hasHistoricals
                      ? ALLOCATOR_MAIN_WIDTH_WITH_HISTORICALS
                      : ALLOCATOR_MAIN_WIDTH
                }
                isMainLeftPanelOpen={isMainLeftPanelOpen}
                isAllocatorOpen={isAllocatorPanelOpen}
                isLeftBlockConfigPanelOpen={isLeftBlockConfigPanelOpen}
                isAskVennOpen={isAskVennOpen}
                inPrintMode={inPrintMode}
                orientation={printOrientationType}
              >
                {!inPrintMode && (
                  <TopControl>
                    <SaveWrapper>
                      <RedesignedTopBar openReportConfigModal={openReportConfigModal} />
                    </SaveWrapper>
                  </TopControl>
                )}

                <StudioPanels
                  inPrintMode={inPrintMode}
                  isLeftBlockConfigPanelOpen={isLeftBlockConfigPanelOpen}
                  isAllocatorPanelOpen={isAllocatorPanelOpen}
                />

                <StudioMainContent inPrintMode={inPrintMode} isReport={isReport} />

                {hasAskVenn && !inPrintMode && isAskVennOpen && <AskVennPanel />}

                {!inPrintMode && isOpenReportConfigModal && (
                  <SetupConfigurationModal onCancel={closeReportConfigModal} />
                )}
                {viewId && <InaccessibleSubjectsWarningModal viewId={viewId} />}
              </PageContent>
              <ProxyXLSExport />
              <VdparXLSExport />
            </SingleProxyPickerContext.Provider>
          </ContentWrapper>
          {hasProxyVisibilityFF && modalManager.activeInvestment && (
            <ProxyPickerSidePanel
              investments={[modalManager.activeInvestment]}
              handleClose={modalManager.close}
              onProxyChange={handleProxyChange}
              activeModal={modalManager.activeModal}
            />
          )}
        </>
      )}
    </UnsavedChangesTrigger>
  );
});

export default StudioContent;

const secondaryLeftPanelMixin = css`
  background-color: ${GetColor.GreyScale.Grey10};
  padding-left: 0px;
  height: 100%;
`;

const BlockConfigPanelWrapper = styled.div`
  ${secondaryLeftPanelMixin}
  grid-area: blockConfigPanel;
  overflow: auto;
`;

const AllocationPanelWrapper = styled.div`
  grid-area: allocator;
  position: relative;
  overflow: hidden;
  height: 100%;
  padding-left: 0px;
`;

const SaveWrapper = styled.div`
  height: ${TOP_TOOLBAR_HEIGHT};
`;

const Blocks = styled.div<{ inPrintMode?: boolean }>`
  background-color: ${ColorUtils.hex2rgbaFrom(GetColor.GreyScale.Grey10, 0.5)};
  ${({ inPrintMode }) =>
    inPrintMode &&
    css`
      background-color: transparent;
      padding: 0;
    `}
`;

const ContentWrapper = styled.div<{ height?: number }>`
  overflow-x: hidden;
  position: relative;
  margin: 0 auto;
  width: 100%;

  @media print {
    display: block !important;
  }
`;

const StudioStyle = createGlobalStyle<{ pageSize: string }>`
  /* Printing */
  @media print {
    size: ${({ pageSize }) => pageSize};
  }

  @page {
    size: ${({ pageSize }) => pageSize};
    margin: 0;
    padding: 0;
  }
`;

const StyledExternalActivityListener = styled(ExternalActivityListener)<{ allowOverflow: boolean }>`
  ${({ allowOverflow }) =>
    allowOverflow &&
    css`
      overflow-y: auto;
    `}
  ${ContentStyle}
`;
